import { memo, useMemo, Fragment, useEffect, useRef } from "react";
import { Divider, Flex, Typography } from "antd";
import styled from "styled-components";
import { useTranslation } from "react-i18next";
import type { SectionWithSlides } from "hooks/useStoryboardWithSlides";
import { StreamingSlidePreview } from "./";
import type { Templates } from "pages/media/components/storyboardEditor/types";
import type { MediaGenerationSettingsResponse } from "pages/media/types";
import { useAntTheme } from "hooks/useAntTheme";
import { SlideSkeleton } from "./SlideSkeleton";
import { useBrandKit } from "hooks/useBrandKit/";
import { useAutoScroll } from "./hooks/useAutoScroll";
import { radius } from "@libertify/ui";

// Constants for thumbnail sizing
const THUMBNAIL_PANEL_WIDTH = 208;
const THUMBNAIL_PANEL_PADDING = 16;
const THUMBNAIL_BORDER_WIDTH = 3;

const getThumbnailSize = (aspectRatio: number) => ({
  width:
    THUMBNAIL_PANEL_WIDTH -
    THUMBNAIL_PANEL_PADDING * 2 -
    THUMBNAIL_BORDER_WIDTH * 2,
  height: Math.round(
    (THUMBNAIL_PANEL_WIDTH -
      THUMBNAIL_PANEL_PADDING * 2 -
      THUMBNAIL_BORDER_WIDTH * 2) /
      aspectRatio
  ),
});

// Styled components for scrollable behavior (similar to StoryboardSlidesThumbnails)
const StyledScrollableFlex = styled(Flex)`
  padding-inline: ${THUMBNAIL_PANEL_PADDING}px;
  padding-block-end: 12px;
  height: 100%;
  overflow-y: scroll;
  flex-shrink: 0;
  width: min-content;

  /* Firefox */
  scrollbar-width: thin;
  scrollbar-color: ${({ theme }) => theme.colorBorder} transparent;

  /* Chrome, Safari, Edge */
  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${({ theme }) => theme.colorBorder};
    border-radius: 3px;
    opacity: 0.5;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: ${({ theme }) => theme.colorTextDescription};
    opacity: 0.7;
  }

  .ant-card-hoverable {
    border-color: #00000000;
  }
`;

const StickyChapterLabel = styled.div`
  position: sticky;
  top: 0;
  background-color: ${({ theme }) => theme.colorBgContainer};
  padding-block: 8px;
  z-index: 10;
  font-weight: 500;
  text-align: center;
  opacity: 0.9;
`;

const LayoutWrapper = styled(Flex)`
  position: relative;
  flex-direction: column;
  align-items: center;
  gap: 6px;
`;

export interface ThumbnailPanelProps {
  sections: SectionWithSlides[];
  isLoading: boolean;
  activeSlideId: string | null;
  onSlideSelect: (slideId: string) => void;
  templates: Templates;
  slideNameToTemplateIdMap: Record<string, string>;
  mediaAspectRatio: number;
  mediaSettings?: MediaGenerationSettingsResponse;
  brandKitData: ReturnType<typeof useBrandKit>;
  skipReveal?: boolean;
}

export const ThumbnailPanel = memo(
  ({
    sections,
    isLoading,
    activeSlideId,
    onSlideSelect,
    templates,
    slideNameToTemplateIdMap,
    mediaAspectRatio,
    mediaSettings,
    brandKitData,
    skipReveal = false,
  }: ThumbnailPanelProps) => {
    const { theme } = useAntTheme();
    const { t } = useTranslation();

    const templateSize = useMemo(
      () => getThumbnailSize(mediaAspectRatio),
      [mediaAspectRatio]
    );

    const scrollContainerRef = useRef<HTMLDivElement>(null);
    const { scrollToElement } = useAutoScroll(scrollContainerRef);
    const slideRef = useRef<HTMLDivElement>(null);

    // Track revealed thumbnails — only grows, never shrinks (clicking an
    // earlier thumbnail should not hide later ones that were already shown)
    const revealedSlideIdsRef = useRef(new Set<string>());

    useMemo(() => {
      if (!activeSlideId) return;
      for (const section of sections) {
        for (const slide of section.slides) {
          // eslint-disable-next-line react-hooks/refs
          revealedSlideIdsRef.current.add(slide.uuid);
          if (slide.uuid === activeSlideId) return;
        }
      }
    }, [sections, activeSlideId]);

    // eslint-disable-next-line react-hooks/refs
    const revealedSlideIds = revealedSlideIdsRef.current;

    useEffect(() => {
      if (slideRef.current && activeSlideId) {
        scrollToElement(slideRef.current, {
          behavior: "smooth",
          block: "center",
        });
      }
    }, [activeSlideId, scrollToElement]);

    return (
      <Flex
        style={{
          borderRadius: `${radius.box}px ${radius.box}px 0 0`,
          height: "calc(100% - 24px)",
          marginBlockStart: 24,
          marginInlineStart: 24,
          overflowY: "hidden",
          paddingInline: 0,
          paddingBlockStart: THUMBNAIL_PANEL_PADDING,
          backgroundColor: theme.colorBgContainer,
          zIndex: 50,
        }}
      >
        <StyledScrollableFlex ref={scrollContainerRef} vertical gap={8}>
          {isLoading && sections.length === 0 ? (
            <SlideSkeleton {...templateSize} />
          ) : (
            // eslint-disable-next-line react-hooks/refs
            sections.map((section, sectionIndex) => {
              const currentChapterSettings = mediaSettings?.chapters.find(
                (chapter) => chapter.chapter_id === section.id
              );
              const transitionTemplateId =
                currentChapterSettings?.transition_template_id ?? undefined;

              if (!currentChapterSettings)
                return (
                  <Fragment key={section.id}>
                    <Typography.Text
                      type="secondary"
                      style={{
                        fontSize: 11,
                        textTransform: "uppercase",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        width: "100%",
                      }}
                    >
                      {section.topic}
                    </Typography.Text>
                    <SlideSkeleton {...templateSize} />
                  </Fragment>
                );

              return (
                <Flex key={section.id} vertical gap={16}>
                  <StickyChapterLabel>
                    <Typography.Text
                      strong
                      style={{ fontSize: "14px", textTransform: "uppercase" }}
                    >
                      {t(
                        "components.templates.TemplatesBoard.storyboardSlidesThumbnails.chapter",
                        { number: sectionIndex + 1 }
                      )}
                    </Typography.Text>
                  </StickyChapterLabel>

                  {section.slides.map((slide, slideIndex) => {
                    const template = templates.get(slide.uuid!);
                    const slideTemplateId =
                      slideNameToTemplateIdMap[slide.uuid!];
                    const hasTemplate = template && slideTemplateId;
                    const isRevealed = revealedSlideIds.has(slide.uuid!);
                    const isActive = activeSlideId === slide.uuid!;
                    const isTransitionSlide =
                      slideTemplateId === transitionTemplateId;

                    if (!hasTemplate || (!skipReveal && !isRevealed))
                      return (
                        <SlideSkeleton key={slide.uuid} {...templateSize} />
                      );

                    const templatePreview = (
                      <StreamingSlidePreview
                        ref={isActive ? slideRef : undefined}
                        border={{
                          borderColor: isActive
                            ? theme.colorPrimary
                            : undefined,
                          borderRadius: radius.box,
                          borderWidth: 3,
                        }}
                        templateSize={templateSize}
                        onCardClick={() => {
                          onSlideSelect(slide.uuid!);
                        }}
                        slide={slide}
                        template={template}
                        brandKitData={brandKitData}
                      />
                    );

                    return (
                      <Fragment key={slide.uuid}>
                        {isTransitionSlide ? (
                          <LayoutWrapper>
                            <Typography.Text
                              style={{
                                fontSize: "12px",
                                textTransform: "uppercase",
                              }}
                            >
                              {t(
                                "components.templates.TemplatesBoard.storyboardSlidesThumbnails.transitionScene"
                              )}
                            </Typography.Text>
                            {templatePreview}
                          </LayoutWrapper>
                        ) : (
                          <LayoutWrapper>
                            <Typography.Text
                              style={{
                                fontSize: "12px",
                                textTransform: "uppercase",
                              }}
                            >
                              {t(
                                "components.templates.TemplatesBoard.storyboardSlidesThumbnails.scene",
                                {
                                  number:
                                    slideIndex + (transitionTemplateId ? 0 : 1),
                                }
                              )}
                            </Typography.Text>
                            {templatePreview}
                          </LayoutWrapper>
                        )}
                      </Fragment>
                    );
                  })}
                  {sectionIndex < sections.length - 1 && (
                    <Divider
                      style={{
                        marginBlock: "8px",
                        borderBlockEnd: `1px solid ${theme.colorBorder}`,
                        opacity: 1,
                      }}
                    />
                  )}
                </Flex>
              );
            })
          )}
        </StyledScrollableFlex>
      </Flex>
    );
  }
);

ThumbnailPanel.displayName = "ThumbnailPanel";
